import { CheckoutFrame } from '../components/CheckoutFrame';
import { PingDepositNotice } from '../components/PingDepositNotice';
import type { PingDeposit } from '../services/api';
import { supportContextFromLocation } from '../utils/supportContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { AdyenCheckout, Card, Dropin } from '@adyen/adyen-web/auto';
import '@adyen/adyen-web/styles/adyen.css';
import {
  contractConfigApi,
  contractApi,
  milestoneApi,
  systemApi,
  type CheckoutPolicyConsent
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { apiErrorCode, apiErrorText } from '../utils/apiError';
import { contractTitle } from '../utils/contractTitle';
import {
  ETAPPIO_COMPANY,
  ETAPPIO_COMPANY_VISITING_ADDRESS
} from '../constants/company';

type PaymentMethod = 'bank' | 'swish' | 'card';
type Step = 'form' | 'processing' | 'confirmation-pending' | 'adyen' | 'success' | 'blocked' | 'ping';
type AdyenCompletion = { sessionId?: string; sessionResult?: string };

type AdyenCheckoutSession = {
  id: string;
  sessionData: string;
};

type CheckoutPolicyVersions = {
  termsOfService: string;
  refundPolicy: string;
};

export const CHECKOUT_POLICY_CONSENT_VERSION = '1';

const banks = [
  { id: 'swedbank', name: 'Swedbank', color: '#FF6600' },
  { id: 'seb', name: 'SEB', color: '#60CD18' },
  { id: 'handelsbanken', name: 'Handelsbanken', color: '#005FA0' },
  { id: 'nordea', name: 'Nordea', color: '#0000A0' },
  { id: 'danske', name: 'Danske Bank', color: '#003755' },
  { id: 'lansforsakringar', name: 'Länsförsäkringar', color: '#005CA5' },
];

const validAdyenResultValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return undefined;
  return trimmed;
};

const isExpiredAdyenSessionError = (
  value: unknown,
  depth = 0,
  seen = new Set<object>()
): boolean => {
  if (depth > 5 || value == null) return false;
  if (typeof value === 'string') {
    return /14_0423|the session has expired/i.test(value);
  }
  if (typeof value !== 'object' || seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.some(item => isExpiredAdyenSessionError(item, depth + 1, seen));
  }
  return Object.entries(value).some(([key, item]) =>
    isExpiredAdyenSessionError(key, depth + 1, seen) ||
    isExpiredAdyenSessionError(item, depth + 1, seen)
  );
};

function CheckoutLegalFooter({ provider }: { provider: 'Etappio' | 'Adyen' }) {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    <footer className="checkout-legal-footer">
      <p className="checkout-legal-powered">
        Powered by <strong>{provider}</strong> &middot; {t('paymentCheckout.footerEncrypted')}
      </p>

      <address data-testid="company-legal-details" className="checkout-legal-address">
        <strong className="checkout-legal-name">
          {ETAPPIO_COMPANY.legalName}
        </strong>
        <div className="checkout-legal-row">
          <span className="checkout-legal-nowrap">
            {t('paymentCheckout.footerOrgNr', { number: ETAPPIO_COMPANY.organizationNumber })}
          </span>
          <span className="checkout-legal-nowrap">
            {t('paymentCheckout.footerTradeName', { name: ETAPPIO_COMPANY.tradeName })}
          </span>
        </div>
        <div className="checkout-legal-row">
          <span className="checkout-legal-nowrap">
            {t('companyDetails.visitingAddress')}: {ETAPPIO_COMPANY_VISITING_ADDRESS}
          </span>
          <a href={`mailto:${ETAPPIO_COMPANY.email}`} className="checkout-legal-email">
            {ETAPPIO_COMPANY.email}
          </a>
        </div>
      </address>

      <nav aria-label={t('paymentCheckout.footerLegalNavAria')} className="checkout-legal-nav">
        <Link to="/kontakt" state={{ supportContext: supportContextFromLocation(location) }} className="checkout-legal-nowrap">
          {t('paymentCheckout.footerLinkContact')}
        </Link>
        <Link to="/anvandarvillkor" target="_blank" rel="noopener noreferrer" className="checkout-legal-nowrap">
          {t('paymentCheckout.footerLinkTerms')}
        </Link>
        <Link to="/aterbetalning-och-avbokning" target="_blank" rel="noopener noreferrer" className="checkout-legal-nowrap">
          {t('paymentCheckout.footerLinkRefund')}
        </Link>
      </nav>
    </footer>
  );
}

// Payment checkout page - Adyen-inspired mock
export function PaymentCheckout() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const projectId = searchParams.get('projectId') || '';
  const requestedMethod = searchParams.get('method');
  const method: PaymentMethod =
    requestedMethod === 'swish' || requestedMethod === 'card'
      ? requestedMethod
      : 'bank';
  // Customer funding is scoped to a contract. Legacy milestone parameters are
  // intentionally ignored because milestones only govern later release.
  const contractId = searchParams.get('contractId') || undefined;
  const milestoneId: string | undefined = undefined;
  const requestedAmount = Number(searchParams.get('amountOre'));
  const amountOre = Number.isSafeInteger(requestedAmount) && requestedAmount > 0 ? requestedAmount : undefined;
  const invalidRequestedAmount = searchParams.has('amountOre') && amountOre === undefined;
  const paymentId = searchParams.get('paymentId') || undefined;
  const sessionId = searchParams.get('sessionId') || undefined;
  const redirectResult = validAdyenResultValue(searchParams.get('redirectResult'));
  const projectBasePath = user?.personaType === 'person'
    ? '/person/projects'
    : user?.isAdmin
      ? '/admin/projects'
      : '/projects';
  const projectTransactionsPath = useCallback(
    (paymentStatus?: 'completed' | 'registered', paymentPicker?: 'open') =>
      `${projectBasePath}/${projectId}?tab=transactions${
        paymentStatus ? `&paymentStatus=${paymentStatus}` : ''
      }${paymentPicker ? `&paymentPicker=${paymentPicker}` : ''}`,
    [projectBasePath, projectId]
  );

  const returnToPingProject = useCallback(() => {
    const params = new URLSearchParams({ tab: 'contract' });
    if (contractId) params.set('contractId', contractId);
    navigate(`${projectBasePath}/${projectId}?${params}`, { replace: true });
  }, [contractId, navigate, projectBasePath, projectId]);

  const [checkoutProvider, setCheckoutProvider] = useState<string | null>(null);
  const [pingDeposit, setPingDeposit] = useState<PingDeposit | null>(null);
  const startingPayment = useRef(false);
  const [step, setStep] = useState<Step>('processing');
  const [selectedBank, setSelectedBank] = useState('');
  const [personnummer, setPersonnummer] = useState('');
  const [phone, setPhone] = useState('');
  const [eco, setEco] = useState<any>(null);
  const [contractInfo, setContractInfo] = useState<any>(null);
  const [milestoneInfo, setMilestoneInfo] = useState<any>(null);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [adyenSession, setAdyenSession] = useState<AdyenCheckoutSession | null>(null);
  const [adyenRuntimeClientKey, setAdyenRuntimeClientKey] = useState<string | null>(null);
  const [adyenSessionPaymentId, setAdyenSessionPaymentId] = useState<string | null>(null);
  const [adyenEffectiveMode, setAdyenEffectiveMode] = useState<'mock' | 'sandbox' | 'live' | null>(null);
  const [adyenCheckoutEnvironment, setAdyenCheckoutEnvironment] = useState<'test' | 'live' | null>(null);
  const [adyenError, setAdyenError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [adyenDropinReady, setAdyenDropinReady] = useState(false);
  const [isCheckoutFinalizing, setIsCheckoutFinalizing] = useState(Boolean(redirectResult));
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [policyVersions, setPolicyVersions] = useState<CheckoutPolicyVersions | null>(null);
  const [policyVersionsLoading, setPolicyVersionsLoading] = useState(true);
  const [policyVersionsError, setPolicyVersionsError] = useState(false);
  const adyenContainerRef = useRef<HTMLDivElement | null>(null);
  const adyenComponentRef = useRef<{ unmount?: () => void } | null>(null);
  const expiredSessionRetryRef = useRef(false);
  const checkoutCompletionRef = useRef<AdyenCompletion>();
  const syncingCheckout = useRef(false);
  const checkedAdyenReturn = useRef<string>();

  const loadPolicyVersions = useCallback(async () => {
    setPolicyVersionsLoading(true);
    setPolicyVersionsError(false);
    setPoliciesAccepted(false);
    try {
      const [terms, refundPolicy] = await Promise.all([
        systemApi.getTermsOfService(),
        systemApi.getRefundPolicy()
      ]);
      if (!terms.version || !refundPolicy.version) {
        throw new Error('Policyversion saknas.');
      }
      setPolicyVersions({
        termsOfService: terms.version,
        refundPolicy: refundPolicy.version
      });
    } catch {
      setPolicyVersions(null);
      setPolicyVersionsError(true);
    } finally {
      setPolicyVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPolicyVersions();
  }, [loadPolicyVersions]);

  useEffect(() => {
    let cancelled = false;
    setEco(null);
    if (projectId && checkoutProvider === 'adyen' && !invalidRequestedAmount) {
      (amountOre === undefined
        ? contractConfigApi.getProjectEconomics(projectId, method, contractId, milestoneId)
        : contractConfigApi.getProjectEconomics(projectId, method, contractId, milestoneId, amountOre))
        .then(value => { if (!cancelled) setEco(value); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [projectId, method, contractId, milestoneId, checkoutProvider, amountOre, invalidRequestedAmount]);

  useEffect(() => {
    if (contractId) {
      contractApi.getContract(contractId).then(setContractInfo).catch(() => setContractInfo(null));
    } else {
      setContractInfo(null);
    }
  }, [contractId]);

  useEffect(() => {
    if (contractId && milestoneId) {
      milestoneApi.getMilestones(contractId)
        .then(milestones => setMilestoneInfo(milestones.find(milestone => milestone.id === milestoneId) || null))
        .catch(() => setMilestoneInfo(null));
    } else {
      setMilestoneInfo(null);
    }
  }, [contractId, milestoneId]);

  // For a contract the API prices what is still to be deposited: an ÄTA is a
  // cumulative version, so its own amount includes the funded original
  // contract (ClickUp 869fgj3uh). Fees come from the project-level config.
  const milestoneAmountValue = milestoneInfo ? milestoneInfo.amount || 0 : 0;
  const projectValue = milestoneInfo
    ? milestoneAmountValue
    : (eco?.projectValue || 0);
  const customerFee = eco?.customerFee || 0;
  // Show what the customer is actually charged (customerProjectFee), not the
  // config's raw project fee — otherwise the line shows e.g. 300 kr even when
  // the company covers it and the customer pays nothing.
  const projectFee = eco?.configType === 'partner' ? 0 : (eco?.customerProjectFee ?? eco?.projectFee ?? 0);
  const minimumFeeAdjustment = eco?.minimumFeeAdjustment || eco?.customerMinimumFeeAdjustment || eco?.minimum_fee_adjustment_sek || 0;
  const transactionFee = Math.max(customerFee - projectFee - minimumFeeAdjustment, 0);
  const total = projectValue + customerFee;

  const fmt = (v: number) => new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(v);

  const adyenClientKey = adyenRuntimeClientKey || '';

  const adyenReturnUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    params.set('method', method);
    if (contractId) params.set('contractId', contractId);
    if (milestoneId) params.set('milestoneId', milestoneId);
    if (amountOre !== undefined) params.set('amountOre', String(amountOre));
    return `${window.location.origin}/payment-checkout?${params.toString()}`;
  }, [contractId, method, milestoneId, projectId, amountOre]);

  const adyenPaymentMethodType = method === 'card'
    ? 'scheme'
    : method === 'swish'
      ? 'swish'
      : 'trustly';

  const startPayment = useCallback(async (
    options?: { forceNewSession?: boolean }
  ) => {
    if (invalidRequestedAmount) {
      setAdyenError(t('projectFunding.account.invalidCheckoutAmount'));
      return;
    }
    if (!policiesAccepted || !policyVersions) {
      setAdyenError(t('paymentCheckout.errorMustAcceptTerms'));
      return;
    }
    const policyConsent: CheckoutPolicyConsent = {
      accepted: true,
      termsOfServiceVersion: policyVersions.termsOfService,
      refundPolicyVersion: policyVersions.refundPolicy,
      checkoutVersion: CHECKOUT_POLICY_CONSENT_VERSION
    };
    if (startingPayment.current) return;
    startingPayment.current = true;
    checkoutCompletionRef.current = undefined;
    setStep('processing');
    setAdyenError(null);
    setBlockedMessage(null);
    setAdyenDropinReady(false);
    setIsCheckoutFinalizing(false);
    try {
      const result = options || amountOre !== undefined
        ? await contractConfigApi.initiatePayment(
            projectId,
            checkoutProvider === 'ping' ? 'bank' : method,
            contractId,
            milestoneId,
            adyenReturnUrl(),
            policyConsent,
            { ...options, amountOre }
          )
        : await contractConfigApi.initiatePayment(
            projectId,
            checkoutProvider === 'ping' ? 'bank' : method,
            contractId,
            milestoneId,
            adyenReturnUrl(),
            policyConsent
          );
      if (result?.blockedReason === 'adyen_onboarding_incomplete') {
        setBlockedMessage(
          result.message ||
          t('paymentCheckout.blockedDefaultMessage')
        );
        setStep('blocked');
        return;
      }
      if (result?.paymentRuntime?.provider === 'ping' && result?.pingDeposit) {
        setPingDeposit(result.pingDeposit);
        setStep('ping');
        setSearchParams(params => { const next = new URLSearchParams(params); next.set('paymentId', result.paymentId); next.set('provider', 'ping'); return next; }, { replace: true });
        return;
      }
      const runtime = result?.paymentRuntime;
      if (runtime?.provider !== 'adyen' || !['mock', 'sandbox', 'live'].includes(runtime.effectiveMode)) {
        throw new Error('Payment runtime binding is missing.');
      }
      setAdyenRuntimeClientKey(runtime.checkoutClientKey || null);
      setAdyenEffectiveMode(runtime.effectiveMode);
      setAdyenCheckoutEnvironment(runtime.checkoutEnvironment || null);
      if (runtime?.effectiveMode !== 'mock') {
        if (result?.paymentId && result?.checkoutSession?.id && result.checkoutSession.sessionData) {
          setAdyenSessionPaymentId(result.paymentId);
          setAdyenSession(result.checkoutSession);
          setStep('adyen');
          return;
        }
        setAdyenError(t('paymentCheckout.errorAdyenSessionMissing'));
        setStep('form');
        return;
      }
      await contractConfigApi.completePayment(
        projectId,
        method,
        contractId,
        milestoneId,
        policyConsent
      );
      navigate(projectTransactionsPath('completed'), { replace: true });
    } catch (e) {
      if (apiErrorCode(e)?.startsWith('PING_')) {
        const code = apiErrorCode(e);
        setAdyenError(t(code === 'PING_COMPANY_KYC_REQUIRED' ? 'pingDeposit.companyKycRequired' :
          code === 'PING_COMPANY_KYC_UNAVAILABLE' ? 'pingDeposit.companyKycUnavailable' :
          code === 'PING_ACCOUNT_IDENTITY_REQUIRED' ? 'pingDeposit.identityRequired' :
          code === 'PING_ACCOUNT_PROVISIONING_UNCERTAIN' ? 'pingDeposit.accountUncertain' :
          code?.startsWith('PING_DEPOSIT_DOCUMENTS_') ? 'pingDeposit.documentsError' : 'pingDeposit.startError'));
        setStep('form');
        return;
      }
      if (apiErrorCode(e) === 'PAYMENT_PROVIDER_NOT_IMPLEMENTED') {
        setAdyenError(t('paymentCheckout.errorPaymentProviderUnavailable'));
        setStep('form');
        return;
      }
      if (apiErrorCode(e) === 'PAYMENT_PROVIDER_EXECUTION_DISABLED') {
        setAdyenError(t('paymentCheckout.errorPaymentRuntimeDisabled'));
        setStep('form');
        return;
      }
      setAdyenError(apiErrorText(t, e, 'paymentCheckout.errorPaymentCouldNotStart'));
      void loadPolicyVersions();
      setStep('form');
    } finally { startingPayment.current = false; }
  }, [checkoutProvider, setSearchParams, adyenReturnUrl, contractId, loadPolicyVersions, method, milestoneId, navigate, policiesAccepted, policyVersions, projectId, projectTransactionsPath, t, amountOre, invalidRequestedAmount]);

  const syncCheckoutPaymentAndNavigate = useCallback(async (completion?: AdyenCompletion) => {
    if (syncingCheckout.current) return;
    syncingCheckout.current = true;
    checkoutCompletionRef.current = completion ?? checkoutCompletionRef.current;
    const completed = checkoutCompletionRef.current;
    setIsCheckoutFinalizing(true);
    setStep('processing');
    setAdyenSession(null);
    setAdyenDropinReady(false);
    adyenComponentRef.current?.unmount?.();
    adyenComponentRef.current = null;
    try {
      const input = {
        projectId,
        paymentMethod: method,
        contractId,
        milestoneId,
        // The webhook may already have confirmed this payment. Keep the
        // identity returned at initiation instead of searching pending rows.
        paymentId: adyenSessionPaymentId ?? paymentId,
        sessionId: completed?.sessionId ?? sessionId,
        sessionResult: validAdyenResultValue(completed?.sessionResult),
        redirectResult
      };
      let payment;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          payment = await contractConfigApi.syncAdyenCheckoutPayment(input);
          break;
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response?.status;
          const transient = status === undefined || [408, 502, 503, 504].includes(status);
          if (attempt === 1 || !transient) throw error;
        }
      }
      const paymentMethodRegistered =
        payment?.metadata?.adyenSync?.paymentMethodRegistered === true;
      const paymentCancelled =
        payment?.status === 'cancelled' ||
        payment?.metadata?.adyenSync?.paymentMethodStatus === 'cancelled';
      if (paymentCancelled) {
        navigate(projectTransactionsPath(undefined, 'open'), { replace: true });
        return;
      }
      if (payment?.status === 'pending' && !paymentMethodRegistered) {
        setStep('confirmation-pending');
        return;
      }
      const paymentStatus = payment?.status === 'held'
        ? 'completed'
        : paymentMethodRegistered
          ? 'registered'
          : undefined;
      navigate(projectTransactionsPath(paymentStatus), { replace: true });
    } catch {
      setStep('confirmation-pending');
    } finally {
      syncingCheckout.current = false;
    }
  }, [adyenSessionPaymentId, contractId, method, milestoneId, navigate, paymentId, projectId, projectTransactionsPath, redirectResult, sessionId]);

  useEffect(() => {
    if (!projectId || redirectResult) return;
    let cancelled = false;

    const prepareCheckout = async () => {
      try {
        const context = await contractConfigApi.getCheckoutContext(projectId, contractId);
        if (cancelled) return;
        if (context.provider === 'ping' || (searchParams.get('provider') === 'ping' && paymentId)) {
          setCheckoutProvider('ping');
          const originalId = searchParams.get('provider') === 'ping' ? paymentId : context.paymentId;
          if (originalId) {
            const result = await contractConfigApi.getPingDeposit(originalId);
            if (cancelled) return;
            setPingDeposit(result.pingDeposit); setStep('ping');
          } else setStep('form');
          return;
        }
        setCheckoutProvider(context.provider);
      } catch {
        if (!cancelled) { setAdyenError(t('pingDeposit.contextError')); setStep('form'); }
        return;
      }
      const runtime = await contractConfigApi.getAdyenRuntime().catch(() => null);
      if (cancelled) return;
      setAdyenRuntimeClientKey(runtime?.checkoutClientKey || null);
      setAdyenEffectiveMode(runtime?.effectiveMode || null);
      setAdyenCheckoutEnvironment(runtime?.checkoutEnvironment || null);
      if (method === 'card' && runtime?.cardPaymentsEnabled !== true) {
        navigate(projectTransactionsPath(), { replace: true });
        return;
      }
      setStep('form');
    };

    void prepareCheckout();

    return () => {
      cancelled = true;
    };
  }, [contractId, method, navigate, paymentId, projectId, projectTransactionsPath, redirectResult, searchParams, t]);

  useEffect(() => {
    if (!projectId || !redirectResult) return;
    const returnedCheckout = JSON.stringify([projectId, contractId, milestoneId, paymentId, sessionId, redirectResult]);
    if (checkedAdyenReturn.current === returnedCheckout) return;
    checkedAdyenReturn.current = returnedCheckout;
    void syncCheckoutPaymentAndNavigate();
  }, [contractId, milestoneId, paymentId, projectId, redirectResult, sessionId, syncCheckoutPaymentAndNavigate]);

  useEffect(() => {
    if (step !== 'adyen' || !adyenSession || !adyenContainerRef.current) return;
    if (!adyenClientKey) {
      setAdyenError(t('paymentCheckout.errorClientKeyMissing'));
      return;
    }
    if (!adyenCheckoutEnvironment) {
      setAdyenError(t('paymentCheckout.errorEnvironmentUnknown'));
      return;
    }

    let cancelled = false;
    const readyTimeout = window.setTimeout(() => {
      if (!cancelled) {
        setAdyenError(t('paymentCheckout.errorAdyenNotResponding'));
      }
    }, 12_000);
    const mountAdyenCheckout = async () => {
      try {
        setAdyenError(null);
        setAdyenDropinReady(false);
        adyenComponentRef.current?.unmount?.();
        const checkout = await AdyenCheckout({
          environment: adyenCheckoutEnvironment,
          clientKey: adyenClientKey,
          locale: 'sv-SE',
          countryCode: 'SE',
          session: {
            id: adyenSession.id,
            sessionData: adyenSession.sessionData
          },
          beforeSubmit: async (state, _component, actions) => {
            try {
              if (!adyenSessionPaymentId) throw new Error('Payment binding missing');
              const current = await contractConfigApi.getPaymentRuntime(adyenSessionPaymentId);
              if (current.effectiveMode !== adyenEffectiveMode) {
                actions.reject();
                setAdyenError(t('paymentCheckout.errorPaymentRuntimeDisabled'));
                return;
              }
              setAdyenError(null);
              actions.resolve(state);
            } catch (error) {
              actions.reject();
              setAdyenError(t(apiErrorCode(error) === 'PAYMENT_PROVIDER_EXECUTION_DISABLED'
                ? 'paymentCheckout.errorPaymentRuntimeDisabled'
                : 'paymentCheckout.errorPaymentRuntimeCheckFailed'));
            }
          },
          onPaymentCompleted: (result) => {
            const sessionResult =
              result &&
              'sessionResult' in result
                ? validAdyenResultValue(result.sessionResult)
                : undefined;
            const completedSessionId = adyenSession.id;
            setStep('processing');
            setAdyenSession(null);
            adyenComponentRef.current?.unmount?.();
            adyenComponentRef.current = null;
            void syncCheckoutPaymentAndNavigate({
              sessionId: completedSessionId,
              sessionResult
            });
          },
          onError: (checkoutError) => {
            if (
              isExpiredAdyenSessionError(checkoutError) &&
              !expiredSessionRetryRef.current
            ) {
              expiredSessionRetryRef.current = true;
              void startPayment({ forceNewSession: true });
              return;
            }
            setAdyenError(t('paymentCheckout.errorAdyenCompletionFailed'));
          }
        });
        if (cancelled || !adyenContainerRef.current) return;
        const markReady = () => {
          window.clearTimeout(readyTimeout);
          setAdyenDropinReady(true);
        };
        const cardConfiguration = {
          hasHolderName: true,
          holderNameRequired: true,
          onConfigSuccess: markReady,
          onReady: markReady
        };
        adyenComponentRef.current =
          method === 'card'
            ? new Card(checkout, cardConfiguration).mount(adyenContainerRef.current)
            : new Dropin(checkout, {
                openPaymentMethod: { type: adyenPaymentMethodType },
                openFirstPaymentMethod: true,
                showStoredPaymentMethods: false,
                paymentMethodsConfiguration: {
                  card: cardConfiguration
                },
                onReady: markReady
              }).mount(adyenContainerRef.current);
      } catch (error) {
        if (!cancelled) {
          window.clearTimeout(readyTimeout);
          setAdyenError(t('paymentCheckout.errorAdyenCouldNotStart'));
        }
      }
    };

    void mountAdyenCheckout();

    return () => {
      cancelled = true;
      window.clearTimeout(readyTimeout);
      adyenComponentRef.current?.unmount?.();
      adyenComponentRef.current = null;
    };
  }, [adyenCheckoutEnvironment, adyenClientKey, adyenEffectiveMode, adyenPaymentMethodType, adyenSession, adyenSessionPaymentId, method, startPayment, syncCheckoutPaymentAndNavigate, step, t]);

  const handlePay = () => {
    if (!policiesAccepted) return;
    expiredSessionRetryRef.current = false;
    void startPayment();
  };

  const policyConsentReady = policiesAccepted && Boolean(policyVersions) && Boolean(checkoutProvider);
  const usesMockCheckout = adyenEffectiveMode === 'mock';
  const canSubmitBank = Boolean(selectedBank && personnummer.length >= 10 && policyConsentReady);
  const canSubmitSwish = Boolean(phone.length >= 10 && policyConsentReady);

  // Styling lives in src/App.css (.checkout-* classes, themeable via CSS
  // variables). Only genuinely dynamic values (e.g. a bank's brand colour, or
  // the Adyen Drop-in host's min-height asserted by tests) stay inline below.

  if (step === 'ping' && pingDeposit) return <CheckoutFrame ping onClose={returnToPingProject}>
    <PingDepositNotice key={pingDeposit.paymentId} initial={pingDeposit} canSimulate={Boolean(user?.isAdmin)} onBack={returnToPingProject} onFunded={returnToPingProject} />
  </CheckoutFrame>;

  if (step === 'success') {
    return (
      <div className="checkout-page">
        <div className="checkout-card">
          <div className="checkout-state">
            <div className="checkout-state-icon checkout-state-icon--success">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <h2 className="checkout-state-title">{t('paymentCheckout.successTitle')}</h2>
            <p className="checkout-state-text">
              {t('paymentCheckout.successAmountPaid', { amount: fmt(total) })}
              {milestoneInfo ? t('paymentCheckout.successForMilestone', { number: milestoneInfo.milestoneNumber }) : contractInfo ? t('paymentCheckout.successForContract', { number: contractInfo.contractNumber }) : ''}
              {' '}{t('paymentCheckout.successForProject', { project: eco?.projectNumber ? `#${eco.projectNumber}` : `#${projectId.slice(0, 6)}` })}
            </p>
            <div className="checkout-receipt">
              <div className="checkout-receipt-row">
                <span className="checkout-receipt-label">{t('paymentCheckout.summaryAmount')}</span>
                <span className="checkout-receipt-value">{fmt(total)} kr</span>
              </div>
              <div className="checkout-receipt-row">
                <span className="checkout-receipt-label">{t('paymentCheckout.summaryMethod')}</span>
                <span className="checkout-receipt-value">
                  {method === 'bank' ? t('paymentCheckout.methodBankTransfer') : method === 'card' ? t('paymentCheckout.methodCard') : 'Swish'}
                </span>
              </div>
              <div className="checkout-receipt-row">
                <span className="checkout-receipt-label">{t('paymentCheckout.summaryStatus')}</span>
                <span className="checkout-receipt-value checkout-receipt-value--ok">{t('paymentCheckout.statusCompleted')}</span>
              </div>
            </div>
            <button className="checkout-btn" onClick={() => navigate(-1)}>
              {t('paymentCheckout.backToProject')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'processing') {
    const msg = checkoutProvider === 'ping' ? t('pingDeposit.checking') : redirectResult || isCheckoutFinalizing
      ? t('paymentCheckout.processingPayment')
      : method === 'bank'
        ? t('paymentCheckout.processingWaitingBankId')
        : method === 'swish'
          ? t('paymentCheckout.processingWaitingSwish')
          : t('paymentCheckout.processingPaymentShort');
    return (
      <CheckoutFrame ping={checkoutProvider === 'ping'} busy onClose={returnToPingProject}>
          <div className="checkout-state checkout-state--processing">
            <div className="checkout-spinner"></div>
            <p className="checkout-processing-text">{msg}</p>
          </div>
      </CheckoutFrame>
    );
  }

  if (step === 'confirmation-pending') {
    return (
      <CheckoutFrame ping={false} onClose={() => navigate(projectTransactionsPath(), { replace: true })}>
        <div className="checkout-state" role="status">
          <h2 className="checkout-state-title">{t('paymentCheckout.confirmationPendingTitle')}</h2>
          <p className="text-explain mb-4">{t('paymentCheckout.confirmationPendingText')}</p>
          <button type="button" className="btn btn-primary btn-lg w-100" onClick={() => void syncCheckoutPaymentAndNavigate()}>
            {t('paymentCheckout.checkStatus')}
          </button>
          <button type="button" className="btn btn-link btn-lg w-100 mt-2" onClick={() => navigate(projectTransactionsPath(), { replace: true })}>
            {t('paymentCheckout.backToProject')}
          </button>
        </div>
      </CheckoutFrame>
    );
  }

  if (step === 'blocked') {
    return (
      <div className="checkout-page">
        <div className="checkout-card">
          <div className="checkout-state">
            <div className="checkout-state-icon checkout-state-icon--warning">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v5"></path><path d="M12 16h.01"></path></svg>
            </div>
            <h2 className="checkout-state-title">{t('paymentCheckout.blockedTitle')}</h2>
            <p className="checkout-state-text checkout-state-text--blocked">
              {blockedMessage}
            </p>
            <div className="checkout-note--warning">
              {t('paymentCheckout.blockedNoPaymentStarted')}
            </div>
            <button className="checkout-btn" onClick={() => navigate(projectTransactionsPath(), { replace: true })}>
              {t('paymentCheckout.backToProject')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'adyen') {
    return (
      <div className="checkout-page">
        <div className="checkout-card">
          <div className="checkout-header">
            <div className="checkout-brand">
              <div className="checkout-brand-logo">E</div>
              <span className="checkout-brand-name">Etappio</span>
            </div>
            <div className="checkout-secure">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
              {t('paymentCheckout.securePayment')}
            </div>
          </div>
          <div className="checkout-body checkout-body--adyen">
            <div className="checkout-adyen-wrap">
              {!adyenDropinReady && !adyenError && (
                <div role="status" className="checkout-adyen-loading">
                  <div className="checkout-spinner checkout-spinner--sm"></div>
                  <span className="checkout-adyen-loading-text">{t('paymentCheckout.loadingSecurePayment')}</span>
                </div>
              )}
              <div
                ref={adyenContainerRef}
                data-testid="adyen-dropin"
                className="checkout-adyen-frame"
              />
            </div>
            {adyenError && (
              <div className="alert alert-danger mt-3" role="alert">
                {adyenError}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-3"
                  onClick={() => {
                    expiredSessionRetryRef.current = false;
                    void startPayment({ forceNewSession: true });
                  }}
                >
                  {t('paymentCheckout.tryAgain')}
                </button>
              </div>
            )}
          </div>
          <CheckoutLegalFooter provider="Adyen" />
        </div>
      </div>
    );
  }

  return (
    <CheckoutFrame ping={checkoutProvider === 'ping'} onClose={returnToPingProject}>
        {/* Header */}
        <div className="checkout-header">
          <div className="checkout-brand">
            <div className="checkout-brand-logo">E</div>
            <span className="checkout-brand-name">Etappio</span>
          </div>
          <div className="checkout-secure">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            {t('paymentCheckout.securePayment')}
          </div>
        </div>

        {/* Order summary */}
        {checkoutProvider !== 'ping' && <div className="checkout-summary">
          <button onClick={() => setSummaryOpen(!summaryOpen)} className="checkout-summary-toggle">
            <span className="checkout-summary-label">{t('paymentCheckout.orderSummary')}</span>
            <span className="checkout-summary-total">
              <strong>{fmt(total)} kr</strong>
              <svg className={`checkout-summary-chevron${summaryOpen ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </span>
          </button>
          {summaryOpen && (
            <div data-testid="checkout-fee-breakdown" className="checkout-summary-body">
              <div data-testid="checkout-payment-subject" className="checkout-summary-note">
                {t('paymentCheckout.subjectPrefix')}{' '}
                {milestoneInfo
                  ? t('paymentCheckout.subjectMilestone', { number: milestoneInfo.milestoneNumber, description: milestoneInfo.description })
                  : contractInfo
                    ? t('paymentCheckout.subjectContract', { number: contractInfo.contractNumber, description: contractTitle(contractInfo) })
                    : t('paymentCheckout.subjectProject', { project: eco?.projectNumber ? `#${eco.projectNumber}` : `#${projectId.slice(0, 8)}` })}
              </div>
              <div className="checkout-summary-reserved">
                {t('paymentCheckout.reservedNote')}
              </div>
              <div className="checkout-summary-row">
                <span>{milestoneInfo ? t('paymentCheckout.amountMilestone') : t('paymentCheckout.amountToDeposit')}</span><span>{fmt(projectValue)} kr</span>
              </div>
              {transactionFee > 0 && (
                <div className="checkout-summary-row">
                  <span>{t('paymentCheckout.transactionFee')}{typeof eco?.feePct === 'number' ? ` (${eco.feePct} %)` : ''}</span><span>{fmt(transactionFee)} kr</span>
                </div>
              )}
              {projectFee > 0 && (
                <div className="checkout-summary-row">
                  <span>{t('paymentCheckout.projectFee')}</span><span>{fmt(projectFee)} kr</span>
                </div>
              )}
              {minimumFeeAdjustment > 0 && (
                <div className="checkout-summary-row">
                  <span>{t('paymentCheckout.minimumFeeAdjustment')}</span><span>{fmt(minimumFeeAdjustment)} kr</span>
                </div>
              )}
              <div className="checkout-summary-row">
                <span>{t('paymentCheckout.feesTotal')}</span><span>{fmt(customerFee)} kr</span>
              </div>
              <div className="checkout-summary-row--total">
                <span>{t('paymentCheckout.total')}</span><span>{fmt(total)} kr</span>
              </div>
            </div>
          )}
        </div>

        }

        {/* Payment form */}
        <div className="checkout-body">
          {adyenError && <div className="alert alert-danger" role="alert">{adyenError}</div>}
          <fieldset
            className="checkout-consent"
            aria-describedby="checkout-policy-consent-help checkout-policy-versions"
          >
            <legend className="visually-hidden">{t('paymentCheckout.consentLegend')}</legend>
            {policyVersionsLoading && (
              <p className="small text-muted mb-2" role="status">
                {t('paymentCheckout.loadingPolicyVersions')}
              </p>
            )}
            {policyVersionsError && (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                {t('paymentCheckout.policyVersionsLoadError')}{' '}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => void loadPolicyVersions()}>
                  {t('paymentCheckout.tryAgain')}
                </button>
              </div>
            )}
            <div className="form-check m-0">
              <input
                className="form-check-input"
                id="checkout-policy-consent"
                data-testid="checkout-policy-consent"
                type="checkbox"
                required
                disabled={!policyVersions || policyVersionsLoading}
                checked={policiesAccepted}
                onChange={(event) => setPoliciesAccepted(event.target.checked)}
                aria-describedby="checkout-policy-consent-help checkout-policy-versions"
              />
              <label className="form-check-label checkout-consent-label" htmlFor="checkout-policy-consent">
                {t('paymentCheckout.consentIAccept')}{' '}
                <Link to="/anvandarvillkor" target="_blank" rel="noopener noreferrer">
                  {t('paymentCheckout.consentTermsLink')}
                </Link>{' '}{t('paymentCheckout.consentAnd')}{' '}
                <Link to="/aterbetalning-och-avbokning" target="_blank" rel="noopener noreferrer">
                  {t('paymentCheckout.consentRefundLink')}
                </Link>.
              </label>
            </div>
            <p id="checkout-policy-consent-help" className="text-explain mt-2 mb-0">
              {t('paymentCheckout.consentMandatoryHelp')}
            </p>
            <p id="checkout-policy-versions" className="small text-muted mt-1 mb-0" data-testid="checkout-policy-versions">
              {policyVersions
                ? t('paymentCheckout.policyVersionsInfo', { terms: policyVersions.termsOfService, refund: policyVersions.refundPolicy, checkout: CHECKOUT_POLICY_CONSENT_VERSION })
                : t('paymentCheckout.policyVersionsUnverified')}
            </p>
          </fieldset>

          {/* Bank transfer */}
          {checkoutProvider === 'ping' && <section>
            <h1 className="h4">{t('pingDeposit.title')}</h1>
            <p>{t('pingDeposit.instructions')}</p>
            <p>{t('pingDeposit.ownership')}</p>
            <button type="button" className="btn btn-primary btn-lg w-100" disabled={!policyConsentReady} onClick={handlePay}>{t('pingDeposit.create')}</button>
          </section>}
          {checkoutProvider !== 'ping' && method === 'bank' && (
            <div>
              {usesMockCheckout ? (
                <>
                  <h3 className="checkout-section-title">{t('paymentCheckout.chooseBank')}</h3>
                  <div className="checkout-bank-grid">
                    {banks.map(b => (
                      <button key={b.id} onClick={() => setSelectedBank(b.id)} className={`checkout-bank-btn${selectedBank === b.id ? ' is-selected' : ''}`}>
                        <div className="checkout-bank-icon" style={{ background: b.color }}>
                          <span className="checkout-bank-icon-letter">{b.name[0]}</span>
                        </div>
                        <span className="checkout-bank-name">{b.name}</span>
                      </button>
                    ))}
                  </div>
                  <label className="checkout-label">{t('paymentCheckout.personalNumber')}</label>
                  <input type="text" placeholder="YYYYMMDD-XXXX" value={personnummer} onChange={e => setPersonnummer(e.target.value)} className="checkout-input" />
                  <button onClick={handlePay} disabled={!canSubmitBank} aria-describedby="checkout-policy-consent-help" className="checkout-btn checkout-btn--mt-lg">
                    {t('paymentCheckout.openBankId')}
                  </button>
                </>
              ) : (
                <>
                  <h3 className="checkout-section-title checkout-section-title--tight">{t('paymentCheckout.payViaTrustly')}</h3>
                  <p className="checkout-section-desc">
                    {t('paymentCheckout.trustlyDescription')}
                  </p>
                  <button onClick={handlePay} disabled={!policyConsentReady} aria-describedby="checkout-policy-consent-help" className="checkout-btn">
                    {t('paymentCheckout.continueToTrustly')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Swish */}
          {checkoutProvider !== 'ping' && method === 'swish' && (
            <div>
              <h3 className="checkout-section-title">{t('paymentCheckout.payWithSwish')}</h3>
              {usesMockCheckout ? (
                <>
                  <div className="checkout-swish-box">
                    <div className="checkout-swish-qr">
                      <div className="checkout-swish-qr-inner">
                        <div className="checkout-swish-qr-icon">S</div>
                        <div className="checkout-swish-qr-label">{t('paymentCheckout.qrCode')}</div>
                      </div>
                    </div>
                    <p className="checkout-swish-hint">{t('paymentCheckout.swishScanInstruction')}</p>
                  </div>
                  <div className="checkout-divider">
                    <div className="checkout-divider-line"></div>
                    <span className="checkout-divider-text">{t('paymentCheckout.or')}</span>
                    <div className="checkout-divider-line"></div>
                  </div>
                  <label className="checkout-label">{t('paymentCheckout.mobileNumber')}</label>
                  <input type="tel" placeholder="07X XXX XX XX" value={phone} onChange={e => setPhone(e.target.value)} className="checkout-input" />
                  <button onClick={handlePay} disabled={!canSubmitSwish} aria-describedby="checkout-policy-consent-help" className="checkout-btn checkout-btn--mt-lg">
                    {t('paymentCheckout.sendPaymentRequest')}
                  </button>
                </>
              ) : (
                <>
                  <p className="checkout-section-desc">
                    {t('paymentCheckout.swishDescription')}
                  </p>
                  <button onClick={handlePay} disabled={!policyConsentReady} aria-describedby="checkout-policy-consent-help" className="checkout-btn">
                    {t('paymentCheckout.continueToSwish')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Card */}
          {checkoutProvider !== 'ping' && method === 'card' && (
            <div>
              <h3 className="checkout-section-title">{t('paymentCheckout.payWithCard')}</h3>
              <div className="checkout-card-box">
                <div className="checkout-card-chip">
                  <svg width="34" height="24" viewBox="0 0 34 24" fill="none" aria-hidden="true">
                    <rect x="1" y="3" width="32" height="18" rx="3" stroke="#fff" strokeWidth="2" opacity="0.95" />
                    <path d="M2 9h30" stroke="#fff" strokeWidth="2" opacity="0.95" />
                  </svg>
                </div>
                <p className="checkout-card-hint">{t('paymentCheckout.cardDescription')}</p>
              </div>
              <button onClick={handlePay} disabled={!policyConsentReady} aria-describedby="checkout-policy-consent-help" className="checkout-btn checkout-btn--mt-sm">
                {t('paymentCheckout.continueToCardPayment')}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <CheckoutLegalFooter provider="Etappio" />
    </CheckoutFrame>
  );
}
